import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, User, MapPin, Beef, Globe } from "lucide-react";
import { updateCustomers } from "@/services/auth";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n"; // adjust path if your i18n file is elsewhere

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
];

export default function ProfileSetup() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [villages, setVillages] = useState([]);
  const [selectedLang, setSelectedLang] = useState(i18n.language || "en");

  const mobile =
    localStorage.getItem("mobile") ||
    localStorage.getItem("phone") ||
    localStorage.getItem("phoneNumber") ||
    "";

const [form, setForm] = useState({
  name: "",
  pin: "",
  state: "",
  district: "",
  city: "",
  village: "",
  landowned: "",
});
  const [errors, setErrors] = useState({});

  // ── Language ───────────────────────────────────────────────────────────────
  const handleLanguageChange = (code) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem("appLanguage", code);
  };

  // ── Pincode lookup ─────────────────────────────────────────────────────────
const fetchPincodeData = async (pin) => {
  if (pin.length !== 6) return;
  setPincodeLoading(true);
  setVillages([]);
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const data = await res.json();
    if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
      const postOffices = data[0].PostOffice;
      const po = postOffices[0];
      const villageNames = [...new Set(postOffices.map((p) => p.Name))];
      setVillages(villageNames);
      setForm((prev) => ({
        ...prev,
        state: po.State || "",
        district: po.District || "",
        city: po.Block || po.Division || "", // postal API ka node, jo bhi city ke liye use ho rha
        village: villageNames[0] || "",
      }));
      setErrors((prev) => ({ ...prev, pin: "" }));
    } else {
      setErrors((prev) => ({ ...prev, pin: t("errors.invalidPin") }));
    }
  } catch {
    setErrors((prev) => ({ ...prev, pin: t("errors.pinFetchFail") }));
  }
  setPincodeLoading(false);
};

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const val = name === "pin" ? value.replace(/\D/g, "").slice(0, 6) : value;
    setForm((prev) => ({ ...prev, [name]: val }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "pin" && val.length === 6) fetchPincodeData(val);
  };

  // ── Skip ───────────────────────────────────────────────────────────────────
  const handleSkip = () => {
    navigate("/home");
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("errors.fullNameRequired");
    if (!form.pin || form.pin.length !== 6) newErrors.pin = t("errors.pinInvalid");
    if (form.pin.length === 6 && !form.state) newErrors.pin = t("errors.invalidPin");
    if (!form.landowned) newErrors.landowned = t("errors.cattleRequired");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token_gen"));
      const customerCode = localStorage.getItem("customercode");

   const response = await updateCustomers(token?.access_token, customerCode, {
  name: form.name.trim(),
  pin: parseInt(form.pin),
  state: form.state,
  district: form.district,
  city: form.district,
  village: form.village,
  noofcatel: Number(form.landowned),
});
      if (response?.message === "Request processed successfully.") {
        toast.success(t("success.profileSaved"));
        await dispatch(fetchCustomerData(customerCode));
        localStorage.setItem("isNewUser", "false");
        localStorage.setItem("name", form.name.trim());
        navigate("/home");
      } else {
        toast.error(t("errors.genericDesc"));
      }
    } catch {
      toast.error(t("errors.genericDesc"));
    } finally {
      setLoading(false);
    }
  };

  // ── Fields ─────────────────────────────────────────────────────────────────
  const fields = [
    {
      key: "name",
      label: t("profile.fullName"),
      icon: User,
      inputMode: "text",
      placeholder: t("placeholder.fullName"),
      type: "input",
    },
    {
      key: "pin",
      label: t("profile.pinCode"),
      icon: MapPin,
      inputMode: "numeric",
      placeholder: t("placeholder.pin"),
      maxLength: 6,
      type: "input",
      showSpinner: pincodeLoading,
    },

    {
      key: "landowned",
      label: t("profile.cattle"),
      icon: Beef,
      inputMode: "numeric",
      placeholder: t("placeholder.cattle"),
      type: "input",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center bg-[#F5F6FA]">
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#1A1F36]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col bg-white md:rounded-3xl md:shadow-lg">

        {/* ── Header ── */}
        <div className="relative flex items-center justify-between border-b border-gray-100 px-4 py-4 shrink-0">

          {/* Language selector — left */}
          <div className="flex items-center gap-1.5">
            {/* <Globe className="w-4 h-4 text-[#1A1F36]" />
            <select
              value={selectedLang}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="text-xs font-semibold text-[#1A1F36] bg-transparent outline-none border-none cursor-pointer"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select> */}
          </div>

          {/* Title — center */}
          <p className="text-base font-semibold text-[#1A1F36] absolute left-1/2 -translate-x-1/2 whitespace-nowrap">
            {t("profile.setupHeading")}
          </p>

          {/* Skip — right */}
          {/* <button
            onClick={handleSkip}
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ backgroundColor: "#1A1F3610", color: "#1A1F36" }}
          >
            {t("common.skip")}
          </button> */}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3">

          {/* Mobile info card */}
          <div className="bg-white rounded-2xl border border-gray-100 px-5 py-4 flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "linear-gradient(135deg, #1A1F36 0%, #2d3555 100%)" }}
            >
              <User size={20} className="text-white" />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-[#1A1F36]">{mobile}</p>
              <p className="text-[11px] text-gray-400">{t("profile.setupSubheading")}</p>
            </div>
          </div>

          {/* Fields card — same style as Profile.jsx edit mode */}
          <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4 space-y-4">
            {fields.map((field) => {
              const Icon = field.icon;
              const hasErr = !!errors[field.key];

              return (
                <div key={field.key}>
                  <label
                    className="text-[11px] font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-1.5"
                    style={{ color: "#1A1F3680" }}
                  >
                    <Icon className="w-3 h-3" />
                    {field.label}
                    {field.key === "pin" && pincodeLoading && (
                      <span className="ml-1 text-yellow-600 normal-case font-normal text-[10px] flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {t("signup.fetchingLocation")}
                      </span>
                    )}
                  </label>

                  {field.type === "select" ? (
                    <select
                      name={field.key}
                      value={form[field.key]}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all font-medium bg-white appearance-none"
                      style={{
                        border: hasErr ? "1.5px solid #f87171" : "1.5px solid #1A1F3620",
                        background: hasErr ? "#fff5f5" : "#f9f9fb",
                        color: "#1A1F36",
                      }}
                    >
                      {field.options.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="relative">
                      <input
                        name={field.key}
                        value={form[field.key]}
                        onChange={handleFormChange}
                        placeholder={field.placeholder}
                        inputMode={field.inputMode}
                        maxLength={field.maxLength}
                        readOnly={field.readOnly}
                        className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all font-medium"
                        style={{
                          border: hasErr ? "1.5px solid #f87171" : "1.5px solid #1A1F3620",
                          background: field.readOnly
                            ? "#f3f4f6"
                            : hasErr
                              ? "#fff5f5"
                              : "#f9f9fb",
                          color: hasErr ? "#b91c1c" : "#1A1F36",
                        }}
                      />
                      {field.key === "pin" && pincodeLoading && (
                        <Loader2 className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1F36]" />
                      )}
                    </div>
                  )}

                  {hasErr && (
                    <p className="text-[11px] text-red-500 mt-1 ml-1">{errors[field.key]}</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Info note */}
          <div
            className="rounded-2xl px-4 py-3 flex items-start gap-2"
            style={{ backgroundColor: "#eaf3de", border: "1px solid #a8d08a" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4a7c59" strokeWidth="2" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p className="text-[11px] text-[#4a7c59] leading-relaxed">{t("profile.infoNote")}</p>
          </div>

          {/* Save button */}
          <button
            onClick={handleSubmit}
            disabled={loading || pincodeLoading}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50"
            style={{ backgroundColor: "#1A1F36" }}
          >
            {loading
              ? t("common.pleaseWait")
              : t("profile.saveBtn")}
          </button>

          {/* Skip text link */}
          {/* <button
            onClick={handleSkip}
            className="w-full py-2 text-sm font-semibold text-gray-400 hover:text-[#1A1F36] transition-colors"
          >
            {t("common.skip")}
          </button> */}

        </div>

        <div className="w-full py-3 border-t border-gray-100">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Arjun Oil
          </p>
        </div>
      </div>
    </div>
  );
}